import { isGradient } from "@components/color-picker/lib/detect";
import type {
	ApplyColorStyleOptions,
	ColorTarget,
} from "@components/color-picker/lib/types";
import type { CSSProperties } from "react";

export type { ApplyColorStyleOptions, ColorTarget };

const isEmptyValue = (value: string | undefined): boolean =>
	value === undefined || value === "";

/**
 * Translate a color value (solid hex or gradient CSS string) into the
 * React.CSSProperties needed to render it on the given target surface.
 *
 * For solid values the output is the natural property (backgroundColor,
 * color, borderColor, fill). For gradients, cross-browser shims are applied
 * (backgroundImage, -webkit-background-clip, etc.).
 *
 * Unsupported combinations (return {} with a dev-only console warning):
 * - svgFill + gradient → callers should use getSvgGradientDef.
 * - border + gradient → React inline styles cannot emit the ::before +
 *   mask-composite pattern required to respect border-radius. Callers should
 *   use the styled-components helper `gradientBorderCss` from
 *   @utils/gradient-css (or inline the same pattern).
 *
 * Returns {} for empty/undefined values so callers can safely spread.
 */
export const applyColorStyle = (
	value: string | undefined,
	opts: ApplyColorStyleOptions,
): CSSProperties => {
	if (isEmptyValue(value)) return {};

	const gradient = isGradient(value);

	switch (opts.target) {
		case "background":
			return gradient
				? { backgroundColor: "transparent", backgroundImage: value }
				: { backgroundColor: value };

		case "text":
			return gradient
				? {
						backgroundImage: value,
						backgroundClip: "text",
						WebkitBackgroundClip: "text",
						color: "transparent",
						WebkitTextFillColor: "transparent",
					}
				: { color: value };

		case "border":
			if (gradient) {
				if (import.meta.env.DEV) {
					console.warn(
						"applyColorStyle: border + gradient is not supported directly (border-image breaks border-radius); use gradientBorderCss from @utils/gradient-css instead.",
					);
				}
				return {};
			}
			return { borderColor: value };

		case "svgFill":
			if (gradient) {
				if (import.meta.env.DEV) {
					console.warn(
						"applyColorStyle: svgFill + gradient is not supported directly; use getSvgGradientDef instead.",
					);
				}
				return {};
			}
			return { fill: value };

		default: {
			// Exhaustiveness guard. If a new ColorTarget is added, TS will error here.
			const _exhaustive: never = opts.target;
			void _exhaustive;
			return {};
		}
	}
};
